import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertCircle, CalendarClock, FileText, NotebookPen, Pencil, RefreshCw, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { staffManagerApiService } from '../services/staffManagerApi';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { CreatorProfile } from '../components/CreatorProfile';
import { HubStat, StaffHubHero, StaffHubNav } from '../components/staff/StaffHubNav';
import type { StaffDirectoryEntry, StaffManageAuditRow, StaffTeam } from '../types/staffManager';
import { getStaffPerms, STAFF_MANAGE_PERMISSION } from '../utils/staffPermissions';
import { cn } from '../utils/cn';

interface ActionMeta {
  label: string;
  icon: typeof Activity;
  tone: string;
}

const ACTIONS: Record<string, ActionMeta> = {
  staff_manage_team_assign: { label: 'Added to team', icon: UserPlus, tone: 'bg-green-500/15 text-green-300' },
  staff_manage_team_remove: { label: 'Removed from team', icon: UserMinus, tone: 'bg-red-500/15 text-red-300' },
  staff_manage_team_expiry: { label: 'Changed membership expiry', icon: CalendarClock, tone: 'bg-blue-500/15 text-blue-300' },
  staff_note_create: { label: 'Created a Staff Manager Note', icon: NotebookPen, tone: 'bg-amber-500/15 text-amber-300' },
  staff_note_update: { label: 'Edited a Staff Manager Note', icon: Pencil, tone: 'bg-amber-500/15 text-amber-300' },
  staff_note_delete: { label: 'Deleted a Staff Manager Note', icon: Trash2, tone: 'bg-amber-500/15 text-amber-300' },
};

const actionMeta = (action: string): ActionMeta => ACTIONS[action] ?? { label: action.replace(/_/g, ' '), icon: FileText, tone: 'bg-accent-500/15 text-accent-300' };
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/** Staff Manager · Activity — who changed what (team membership and note actions), newest first. */
export const StaffActivityView = () => {
  useDocumentTitle('Staff Manager Activity');
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getStaffPerms(identity, isDev), [identity, isDev]);

  const [rows, setRows] = useState<StaffManageAuditRow[] | null>(null);
  const [directory, setDirectory] = useState<StaffDirectoryEntry[]>([]);
  const [teams, setTeams] = useState<StaffTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('');

  const load = useCallback(
    async (force = false) => {
      if (force) setRefreshing(true);
      setError(null);
      const [auditRes, dirRes, teamsRes] = await Promise.allSettled([
        staffManagerApiService.getManageAudit({ limit: 150, action: actionFilter || undefined }),
        staffManagerApiService.getDirectory({ force }),
        staffManagerApiService.getTeams({ force }),
      ]);
      if (auditRes.status === 'fulfilled') setRows(auditRes.value.logs);
      else setError(auditRes.reason instanceof Error ? auditRes.reason.message : 'Failed to load activity');
      if (dirRes.status === 'fulfilled') setDirectory(dirRes.value.staff);
      if (teamsRes.status === 'fulfilled') setTeams(teamsRes.value.teams);
      setLoading(false);
      setRefreshing(false);
    },
    [actionFilter],
  );

  useEffect(() => {
    if (perms.canManage) void load();
  }, [perms.canManage, load]);

  const userById = useMemo(() => new Map(directory.map((e) => [e.user_id, e])), [directory]);
  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);
  const actionOptions = useMemo(() => Array.from(new Set((rows ?? []).map((r) => r.action))).sort(), [rows]);
  const stats = useMemo(() => {
    const dayAgo = Date.now() - 86_400_000;
    const weekAgo = Date.now() - 7 * 86_400_000;
    const all = rows ?? [];
    return {
      total: all.length,
      day: all.filter((r) => r.timestamp >= dayAgo).length,
      week: all.filter((r) => r.timestamp >= weekAgo).length,
      denied: all.filter((r) => r.details?.outcome === 'denied').length,
    };
  }, [rows]);

  if (!perms.canManage) {
    return (
      <div className="glass-card p-8 text-center">
        <p className="text-glass-300">The Staff Manager activity feed needs the manage permission.</p>
        <p className="text-glass-500 text-sm mt-1">
          Ask an admin for the <code>{STAFF_MANAGE_PERMISSION}</code> permission.
        </p>
      </div>
    );
  }

  const renderTarget = (row: StaffManageAuditRow) => {
    const d = row.details ?? {};
    const targetUserId = str(d.target_user_id);
    const teamId = str(d.team_id);
    const subjectDiscord = str(d.subject_discord_id);
    const subjectUuid = str(d.subject_uuid);
    const subjectUser = str(d.subject_user_id);
    const parts: React.ReactNode[] = [];

    if (targetUserId) {
      const entry = userById.get(targetUserId);
      const label = entry?.minecraft_username ?? entry?.username ?? `${targetUserId.slice(0, 8)}…`;
      parts.push(
        entry?.discord_id ? (
          <Link key="user" to={`/staff/${entry.discord_id}?tab=teams`} className="font-semibold text-accent-300 hover:underline">
            {label}
          </Link>
        ) : (
          <span key="user" className="font-semibold text-white">
            {label}
          </span>
        ),
      );
    }
    if (teamId) {
      const team = teamById.get(teamId);
      parts.push(
        <span key="team" className="text-glass-300">
          {targetUserId ? ' → ' : ''}
          {team?.display_name ?? `${teamId.slice(0, 8)}…`}
        </span>,
      );
    }
    if (!targetUserId && (subjectDiscord || subjectUuid || subjectUser)) {
      const entry = directory.find((e) => (subjectDiscord && e.discord_id === subjectDiscord) || (subjectUser && e.user_id === subjectUser) || (subjectUuid && e.minecraft_uuid?.toLowerCase() === subjectUuid.toLowerCase()));
      const label = entry?.minecraft_username ?? entry?.username ?? subjectDiscord ?? subjectUuid ?? subjectUser;
      const href = subjectDiscord ?? entry?.discord_id ? `/staff/${subjectDiscord ?? entry?.discord_id}?tab=notes` : subjectUuid ? `/player/${subjectUuid}` : null;
      parts.push(
        href ? (
          <Link key="subject" to={href} className="font-semibold text-accent-300 hover:underline">
            {label}
          </Link>
        ) : (
          <span key="subject" className="font-semibold text-white">
            {label}
          </span>
        ),
      );
    }
    return parts.length > 0 ? parts : <span className="text-glass-500">—</span>;
  };

  const renderExtra = (row: StaffManageAuditRow) => {
    const d = row.details ?? {};
    const bits: string[] = [];
    const outcome = str(d.outcome);
    if (outcome === 'denied') bits.push(`denied${num(d.status) ? ` (${num(d.status)})` : ''}${str(d.error_code) ? ` · ${str(d.error_code)}` : ''}`);
    const assigned = list(d.assigned_groups);
    const removed = list(d.removed_groups);
    if (assigned.length) bits.push(`+${assigned.join(', ')}`);
    if (removed.length) bits.push(`−${removed.join(', ')}`);
    if ('expires_at' in d) {
      const exp = num(d.expires_at);
      bits.push(exp ? `expires ${format(new Date(exp), 'd MMM yyyy')}` : d.expires_at === null ? 'no expiry' : '');
    }
    if (str(d.type)) bits.push(String(d.type));
    const changed = list(d.changed_fields);
    if (changed.length) bits.push(`changed ${changed.join(', ')}`);
    return bits.filter(Boolean).join(' · ');
  };

  return (
    <div className="space-y-6">
      <StaffHubHero
        title="Staff Manager Activity"
        icon={Activity}
        blurb="Every membership change made from ARGUS and — for note readers — every Staff Manager Note action. Note content is never recorded here."
        nav={<StaffHubNav perms={perms} />}
        actions={
          <button type="button" onClick={() => void load(true)} className="glass-button-secondary inline-flex items-center gap-2 text-sm" disabled={refreshing || loading}>
            <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            Refresh
          </button>
        }
        stats={
          rows ? (
            <>
              <HubStat label="Loaded" value={stats.total} sub="most recent actions" />
              <HubStat label="Last 24h" value={stats.day} sub="actions" tone="text-green-300" />
              <HubStat label="Last 7 days" value={stats.week} sub="actions" tone="text-blue-300" />
              <HubStat label="Denied" value={stats.denied} sub="refused by Tesseract" tone={stats.denied > 0 ? 'text-amber-300' : 'text-glass-400'} />
            </>
          ) : undefined
        }
      />

      <div className="glass-card p-4 md:p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <select className="glass-select h-9 py-0 text-sm" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} aria-label="Filter by action">
            <option value="">All actions</option>
            {actionOptions.map((a) => (
              <option key={a} value={a}>
                {actionMeta(a).label}
              </option>
            ))}
          </select>
          <span className="text-xs font-mono text-glass-500 tabular-nums">{rows?.length ?? 0} rows</span>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }, (_, i) => (
              <SkeletonLoader key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (rows?.length ?? 0) === 0 ? (
          <div className="p-10 text-center text-glass-400">
            <Activity className="w-8 h-8 mx-auto mb-2 text-glass-500" />
            Nothing recorded yet.
          </div>
        ) : (
          <ol className="divide-y divide-glass-200/10">
            {rows!.map((row) => {
              const meta = actionMeta(row.action);
              const Icon = meta.icon;
              const denied = row.details?.outcome === 'denied';
              return (
                <li key={row.id} className={cn('flex items-start gap-3 py-3', denied && 'opacity-80')}>
                  <span className={cn('inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 mt-0.5', meta.tone)}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                      <CreatorProfile creatorId={row.actor_id} inline />
                      <span className="text-glass-300">{meta.label.toLowerCase()}</span>
                      {renderTarget(row)}
                      {denied && <span className="inline-flex items-center px-1.5 h-5 rounded-full text-[10px] font-semibold bg-red-500/15 text-red-300 border border-red-500/30">denied</span>}
                    </div>
                    {renderExtra(row) && <div className="text-[11px] font-mono text-glass-500 mt-0.5 truncate">{renderExtra(row)}</div>}
                  </div>
                  <time className="text-[11px] text-glass-500 whitespace-nowrap flex-shrink-0" title={format(new Date(row.timestamp), 'd MMM yyyy HH:mm:ss')} dateTime={new Date(row.timestamp).toISOString()}>
                    {formatDistanceToNow(new Date(row.timestamp), { addSuffix: true })}
                  </time>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};
